import {
    Box,
    TableSortLabel,
    TablePaginationProps,
    TextField,
    TableFooterProps,
    BoxProps,
    Button,
    TextFieldProps,
    TableCellProps,
    SelectChangeEvent,
    Checkbox,
    FormControl,
    FormControlProps,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    TableContainerProps,
    useTheme,
    Typography,
    TypographyProps,
    Chip,
    ListItemButtonProps,
    SvgIcon,
    Tooltip,
} from "@mui/material";
import React, { useEffect, MouseEvent, ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import * as certificateService from "services/certificate-service";
import * as locationService from "services/location-service";
import * as departmentService from "services/department-service";
import * as userService from "services/identityUser-service";

import * as Styles from "pages/employee/certificates/listCertificates/listCertificates.styles";
import dayjs from "dayjs";
import Card, { CardProps } from "@mui/material/Card";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { statusStyleCertificate } from "utilities/colorHelper";
import {
    getSessionStorage,
    parseSessionStorage,
    getLocalStorage,
    pasrseLocalStorage,
} from "utilities/sessionStorage";
import * as authenticationService from "services/AuthenticationService";
import DataTable from "components/formsUI/DataTable";
import {
    GridColumnVisibilityModel,
    GridPaginationModel,
} from "@mui/x-data-grid";
import { useAuthContext } from "contexts/authContext";
import "dayjs/locale/nl";
import "dayjs/locale/fr";
import "dayjs/locale/en";

interface filter {
    lastModificationTime: Date | null;
    dateFrom: Date | null;
    dateTo: Date | null;
    dateCreated: Date | null;
    department: string;
    organisation: string;
    status: string[];
    location: string[];
    referenceCode: string;
    requester: string;
    order: "asc" | "desc";
    orderBy: string;
    currentPage: number;
    rowsPerPage: number;
    // count: number,
}

const initialColumnVisibility: { [key: string]: boolean } = {
    department: false,
    organization: false,
    lastModified: false,
};

const ListCertificates = () => {
    const { user, checkAuth } = useAuthContext();
    const {
        t,
        i18n: { language },
    } = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();
    const dateToday = new Date(
        new Date().getFullYear(),
        new Date().getMonth(),
        new Date().getDate(),
    );
    const [sessionStorageComplete, setSessionStorageComplete] =
        React.useState<boolean>(false);

    // Error
    const [showErrorMessage, setShowErrorMessage] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState("");

    const showError = (message: string) => {
        setErrorMessage(message);
        setShowErrorMessage(true);
    };

    // Table
    const [certificates, setCertificates] = React.useState<
        CertificatePagedDTO[]
    >([]);
    const [currentPage, setCurrentPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(25);
    const [count, setCount] = React.useState(0);
    const [locations, setLocations] = React.useState<LocationList[]>([]);
    const [columns, setColumns] = React.useState<{ [key: string]: boolean }>(
        initialColumnVisibility,
    );

    const [isLoading, setIsLoading] = React.useState(true);
    const [filtersChanged, setFiltersChanged] = React.useState(false);
    const [filtersChangedDate, setFiltersChangedDate] = React.useState("");

    const [order, setOrder] = React.useState<"asc" | "desc">("desc");
    const [orderBy, setOrderBy] = React.useState("creationTime");
    const [dateFrom, setDateFrom] = React.useState<Date | undefined | null>(
        null,
    );
    const [dateTo, setDateTo] = React.useState<Date | undefined | null>(null);
    const [dateCreated, setDateCreated] = React.useState<
        Date | undefined | null
    >(null);
    const [referenceCode, setReferenceCode] = React.useState("");
    const [requester, setRequester] = React.useState("");
    const [initial, setInitial] = React.useState(true);

    // --- Multiselect setup ---
    const [status, setStatus] = React.useState<string[]>([]);
    const [location, setLocation] = React.useState<string[]>([]);
    const [departments, setDepartments] = React.useState<string[]>([]);
    const [selectedDepartments, setSelectedDepartments] = React.useState<
        string[]
    >([]);
    const [organisation, setOrganisation] = React.useState<string>("");
    const [organisations, setOrganisations] = React.useState<string[]>([]);
    const [selectedOrganisations, setSelectedOrganisations] = React.useState<
        string[]
    >([]);
    const [organisationDisabled, setOrganisationDisabled] =
        React.useState<boolean>(false);
    const [lastModificationTime, setLastModificationTime] = React.useState<
        Date | undefined | null
    >(null);
    const [timeouts, setTimeouts] = React.useState<NodeJS.Timeout[]>([]);
    const handleChange = (
        event: SelectChangeEvent<string[]>,
        field: "locations" | "status",
    ) => {
        const {
            target: { value },
        } = event;
        // On autofill we get a stringified value.
        if (field === "locations") {
            const locations =
                typeof value === "string" ? value.split(",") : value;
            setLocation(locations);
            parse({ location: locations });
        } else if (field === "status") {
            const statuses =
                typeof value === "string" ? value.split(",") : value;
            setStatus(statuses);
            parse({ status: statuses });
        }

        setFiltersChanged(true);
    };

    const debounce = () => {
        timeouts.forEach((timeout) => {
            clearTimeout(timeout);
            clearInterval(timeout);
        });
        setTimeouts([]);
        const _timeout = setTimeout(() => {
            getAllInfo();
        }, 500);
        setTimeouts([...timeouts, _timeout]);
    };

    const handleReset = () => {
        setOrder("desc");
        setOrderBy("creationTime");
    };

    const handleColumnVisibility = (columns: GridColumnVisibilityModel) => {
        for (const key of Object.keys(columns)) {
            const value = columns[key] as boolean;
            if (value) continue;
            switch (key) {
                case "referenceCode":
                    setReferenceCode("");
                    break;
                case "dateCreated":
                    setDateCreated(null);
                    break;
                case "dateFrom":
                    setDateFrom(null);
                    break;
                case "dateTo":
                    setDateTo(null);
                    break;
                case "firstNameRequester":
                    setRequester("");
                    break;
                case "organization":
                    setOrganisation("");
                    getOrganisations();
                    break;
                case "department":
                    setSelectedDepartments([]);
                    break;
                case "lastModified":
                    setLastModificationTime(null);
                    break;
                case "locations":
                    setLocation([]);
                    break;
                case "status":
                    setStatus([]);
                    break;
            }
        }
        pasrseLocalStorage<GridColumnVisibilityModel>("certificates", columns);
        setColumns(columns);
    };

    const handleOrgChange = (event: ChangeEvent<HTMLInputElement>): void => {
        setOrganisation(event.target.value);
        parse({ organisation: event.target.value });
        setFiltersChanged(true);
    };

    const handleSelectOrgChange = (event: SelectChangeEvent<string[]>) => {
        const {
            target: { value },
        } = event;
        setSelectedOrganisations(
            typeof value === "string" ? value.split(",") : value,
        );
    };

    const handleDepartmentChange = (event: SelectChangeEvent<string[]>) => {
        const {
            target: { value },
        } = event;
        const departments =
            typeof value === "string" ? value.split(",") : value;
        setSelectedDepartments(departments);
        parse({ department: value });
        setFiltersChanged(true);
    };

    const statuses = checkAuth(
        [
            "BeheerAttesten",
            "IntrekkenAttesten",
            "ControleAttesten",
            "OpvolgenAttesten",
        ],
        false,
    )
        ? [
              "Goed te keuren",
              "Geldig",
              "Toekomstig geldig",
              "Verlopen",
              "Ingetrokken",
          ]
        : checkAuth(["External"])
          ? [
                "Goed te keuren",
                "Geldig",
                "Toekomstig geldig",
                "Verlopen",
                "Ingetrokken",
            ]
          : ["Goed te keuren", "Geldig", "Toekomstig geldig", "Verlopen"];

    const fetchCertificates = (
        currentPage: number,
        rowsPerPage: number,
        order: string,
        orderBy: string,
    ) => {
        setErrorMessage("");
        setShowErrorMessage(false);
        setCertificates([]);
        setIsLoading(true);
        var statusJoin = status.join(",");
        // TODO: Aanpassen
        // Kasper: What the fuck moet er aangepast worden? xoxo
        var locationsJoin = location.join(",");
        const departments = selectedDepartments.join(",");
        const organisationsJoin = selectedOrganisations.join(",");
        certificateService
            .getCertificates(
                currentPage + 1,
                rowsPerPage,
                order,
                orderBy,
                referenceCode,
                dateFrom,
                dateTo,
                dateCreated,
                requester,
                locationsJoin,
                statusJoin,
                departments,
                selectedOrganisations.length > 0
                    ? organisationsJoin
                    : organisation,
                lastModificationTime,
                null,
                undefined,
                undefined,
                organisationDisabled,
            )
            .then((res: CertificatesPagedResponse) => {
                setIsLoading(false);
                setFiltersChanged(false);
                setCount(res.rowCount);
                setCertificates(res.results);
                setFiltersChangedDate("");
            })
            .catch((err: any) => {
                setIsLoading(false);
                const {
                    response: {
                        data: { error },
                    },
                } = err;
                if (error === undefined) {
                    showError(`${t("certificates.error")}`);
                    return;
                }
                const code = error.code as number;
                if (code == 401) {
                    showError(`${t("requests.unauthorized")}`);
                } else {
                    showError(`${t("certificates.error")}`);
                }
            });
    };

    const fetchLocations = () => {
        setErrorMessage("");
        setShowErrorMessage(false);
        locationService
            .getLocations()
            .then((res: LocationList[]) => {
                setLocations(res);
            })
            .catch((error: AxiosError<Error>) => {
                showError(`${t("certificates.error")}`);
            });
    };

    const checkKeyEnter = (event: React.KeyboardEvent) => {
        if (event.key === "Enter") {
            debounce();
            // fetchCertificates(currentPage, rowsPerPage, order, orderBy);
        }
    };

    useEffect(() => {
        if (!sessionStorageComplete && initial) {
            const data = getSessionStorage<filter>("certificates");
            if (data) {
                if (data.lastModificationTime)
                    setLastModificationTime(data.lastModificationTime);
                if (data.dateFrom) setDateFrom(data.dateFrom);
                if (data.dateTo) setDateTo(data.dateTo);
                if (data.dateCreated) setDateCreated(data.dateCreated);
                if (data.department)
                    setSelectedDepartments(
                        typeof data.department === "string"
                            ? data.department.split(",")
                            : data.department,
                    );
                if (data.organisation) setOrganisation(data.organisation);
                if (data.status) setStatus(data.status);
                if (data.referenceCode) setReferenceCode(data.referenceCode);
                if (data.requester) setRequester(data.requester);
                if (data.order) setOrder(data.order);
                if (data.orderBy) setOrderBy(data.orderBy);
                if (data.currentPage) setCurrentPage(data.currentPage);
                if (data.rowsPerPage) setRowsPerPage(data.rowsPerPage);
                // if (data.count) setCount(data.count);
                if (data.location) setLocation(data.location);
                setSessionStorageComplete(true);
            } else {
                setSessionStorageComplete(true);
            }
            getOrganisations();
            const headers =
                getLocalStorage<GridColumnVisibilityModel>("certificates");
            if (headers) setColumns(headers);
        }

        if (sessionStorageComplete) {
            debounce();
            // getAllInfo();
            if (departments.length <= 0) {
                departmentService.getDepartmentNames().then((res: string[]) => {
                    setDepartments([...res]);
                });
            }
        }
    }, [
        dateFrom,
        dateTo,
        dateCreated,
        selectedDepartments,
        selectedOrganisations,
        organisation,
        status,
        referenceCode,
        requester,
        order,
        orderBy,
        currentPage,
        rowsPerPage,
        location,
        lastModificationTime,
        sessionStorageComplete,
    ]);

    const getOrganisations = () => {
        setOrganisationDisabled(false);
        if (checkAuth(["External"]) && !checkAuth(["Employee"])) {
            userService.getCompanyFromUser().then((res: Company[]) => {
                if (res.length <= 0) {
                    setOrganisationDisabled(true);
                    return;
                }

                if (res.length == 1) {
                    setOrganisation(
                        res.map((company: Company) => company.name).join(", "),
                    );
                    setOrganisationDisabled(true);
                } else {
                    // setOrganisation(res.map((company: Company) => company.name)[0]);
                    setSelectedOrganisations(
                        res.map((company: Company) => company.name),
                    );
                    setOrganisations(
                        res.map((company: Company) => company.name),
                    );
                }
            });
        }
    };

    React.useEffect(() => {
        if (user !== null) getOrganisations();
    }, [user]);

    const getAllInfo = () => {
        if (initial) {
            fetchLocations();
            setInitial(false);
        }

        if (filtersChangedDate === "") {
            fetchCertificates(currentPage, rowsPerPage, order, orderBy);
        } else {
            var temp = null;
            if (filtersChangedDate === "dateCreated" && dateCreated)
                temp = new Date(dateCreated).getTime();
            else if (filtersChangedDate === "dateFrom" && dateFrom)
                temp = new Date(dateFrom).getTime();
            else if (filtersChangedDate === "dateTo" && dateTo)
                temp = new Date(dateTo).getTime();
            else if (
                filtersChangedDate === "modification" &&
                lastModificationTime
            ) {
                temp = new Date(lastModificationTime).getTime();
            }
            if (
                (temp && !isNaN(temp)) ||
                (filtersChangedDate === "dateCreated" &&
                    dateCreated === null) ||
                (filtersChangedDate === "dateFrom" && dateFrom === null) ||
                (filtersChangedDate === "dateTo" && dateTo === null) ||
                (filtersChangedDate === "modification" &&
                    lastModificationTime === null)
            ) {
                setFiltersChangedDate("");
                fetchCertificates(currentPage, rowsPerPage, order, orderBy);
            }
        }
    };

    const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.id === "requester") {
            setRequester(event.target.value);
            parse({ requester: event.target.value });
        } else if (event.target.id === "referenceCode") {
            setReferenceCode(event.target.value);
            parse({ referenceCode: event.target.value });
        }
        // else if (event.target.id === "processor") {
        //     setProcessor(event.target.value);
        // }
        setCurrentPage(0);
        setFiltersChanged(true);
    };

    const handleDateFilterChange = (
        date: Date | null | undefined,
        keyboardInputValue: string | undefined,
        field: string = "",
    ) => {
        if (field === "dateCreated") {
            setFiltersChangedDate("dateCreated");
            setDateCreated(date);
            parse({ dateCreated: date });
        } else if (field === "dateFrom") {
            setFiltersChangedDate("dateFrom");
            setDateFrom(date);
            parse({ dateFrom: date });
        } else if (field === "dateTo") {
            setFiltersChangedDate("dateTo");
            setDateTo(date);
            parse({ dateTo: date });
        } else if (field === "modification") {
            setFiltersChangedDate("modification");
            setLastModificationTime(date);
            parse({ lastModificationTime: date });
        }
        setCurrentPage(0);
        setFiltersChanged(true);
    };

    const handleCertificateSort = (property: string) => {
        if (orderBy === property) {
            if (order === "asc") {
                setOrder("desc");
                parse({ order: "desc" });
            } else {
                setOrder("asc");
                parse({ order: "asc" });
            }
            setCurrentPage(0);
        } else {
            setOrderBy(property);
            setOrder("desc");
            parse({ order: "desc", orderBy: property });
            setCurrentPage(0);
        }
    };

    const handleChangeRowsPerPage = (value: number) => {
        setRowsPerPage(value);
        parse({ rowsPerPage: value });
        setCurrentPage(0);
    };

    const handleClickCertificate = (certificate: CertificatePagedDTO) => {
        navigate(`${certificate.id}`);
    };

    const checkFilterUpdateData = () => {
        // if (filtersChanged) {
        //     fetchCertificates(currentPage, rowsPerPage, order, orderBy);
        // }
    };

    const updatePagination = (model: GridPaginationModel) => {
        const { page, pageSize } = model;
        setCurrentPage(page);
        setRowsPerPage(pageSize);
        parse({ currentPage: page, rowsPerPage: pageSize });
    };

    const parse = (filter: { [key: string]: any }): void => {
        const obj: filter = {
            lastModificationTime:
                "lastModificationTime" in filter
                    ? filter.lastModificationTime
                    : lastModificationTime,
            dateFrom: "dateFrom" in filter ? filter.dateFrom : dateFrom,
            department:
                "department" in filter
                    ? filter.department
                    : selectedDepartments,
            organisation:
                "organisation" in filter ? filter.organisation : organisation,
            status: "status" in filter ? filter.status : status,
            referenceCode:
                "referenceCode" in filter
                    ? filter.referenceCode
                    : referenceCode,
            requester: "requester" in filter ? filter.requester : requester,
            order: "order" in filter ? filter.order : order,
            orderBy: "orderBy" in filter ? filter.orderBy : orderBy,
            currentPage:
                "currentPage" in filter ? filter.currentPage : currentPage,
            rowsPerPage:
                "rowsPerPage" in filter ? filter.rowsPerPage : rowsPerPage,
            // count: 'count' in filter ? filter.count : count,
            dateTo: "dateTo" in filter ? filter.dateTo : dateTo,
            dateCreated:
                "dateCreated" in filter ? filter.dateCreated : dateCreated,
            location: "location" in filter ? filter.location : location,
        };
        parseSessionStorage<filter>("certificates", obj);
    };

    // --- Props ---
    const ITEM_HEIGHT = 48;
    const ITEM_PADDING_TOP = 8;
    const tableFilterProps = {
        PaperProps: {
            style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                width: 250,
            },
        },
    };

    const listCertificatesWrapperProps: CardProps = {
        sx: Styles.listCertificatesWrapper(theme),
    };

    const listCertificatesTitleProps: TypographyProps = {
        variant: "h4",
        sx: Styles.listCertificatesTitle(theme),
    };

    const titleWrapperProps: BoxProps = {
        sx: Styles.titleWrapper(theme),
    };

    const tableContainerProps: TableContainerProps = {
        sx: Styles.tableContainer(theme),
    };

    const tableSearchProps: BoxProps = {
        sx: Styles.tableSearch(theme),
    };

    const tableSearchDateProps: BoxProps = {
        sx: {
            width: "110%",
            display: "flex",
            alignContent: "center",
            justifyContent: "center",
            marginTop: "0.50rem",
            paddingBottom: "0.50rem",
        },
    };

    const tableTextFieldProps: TextFieldProps = {
        sx: Styles.tableTextField(theme),
    };

    const organisationsFilterProps: FormControlProps = {
        size: "small",
        sx: Styles.organisationsFilter(theme),
    };

    const statusFilterProps: FormControlProps = {
        size: "small",
        sx: Styles.statusFilter(theme),
    };

    const departmentFilterProps: FormControlProps = {
        size: "small",
        sx: Styles.tableContainer(theme),
    };

    const getStatus = (certificate: CertificatePagedDTO) => {
        // const outOfServiceDate = dayjs(certificate.outOfServiceDate).startOf(
        //     "day",
        // );

        if (certificate.isRevoked) return "Ingetrokken";
        else if (
            !certificate.isRevoked &&
            new Date(certificate.dateTo) < dateToday
        )
            return "Verlopen";
        else if (
            certificate.isApproved &&
            !certificate.isRevoked &&
            new Date(certificate.dateFrom) > dateToday
        )
            return "Toekomstig geldig";
        else if (
            certificate.isApproved &&
            !certificate.isRevoked &&
            new Date(certificate.dateFrom) <= dateToday &&
            new Date(certificate.dateTo) >= dateToday
        )
            return "Geldig";
        else if (!certificate.isApproved && !certificate.isRevoked)
            return "Goed te keuren";

        return "";
    };

    const orgProps: TextFieldProps = {
        size: "small",
        sx: Styles.departmentFilter(theme),
    };

    const lastModificationTimeProps = (
        params: TextFieldProps,
    ): TextFieldProps => {
        return {
            ...orgProps,
            ...params,
            onKeyDown: checkKeyEnter,
        };
    };

    return (
        <Card {...listCertificatesWrapperProps}>
            <Box {...titleWrapperProps}>
                <Typography {...listCertificatesTitleProps}>
                    {t("certificates.overview")}
                </Typography>
                {showErrorMessage && (
                    <Box color="error.main">
                        <Box>{errorMessage}</Box>
                        <Button
                            sx={{ marginTop: "5px" }}
                            color="error"
                            variant="outlined"
                            onClick={() => {
                                getAllInfo();
                            }}
                        >
                            {t("certificates.errorButton")}
                        </Button>
                    </Box>
                )}
            </Box>

            <Box {...tableContainerProps}>
                <DataTable
                    rows={certificates}
                    columns={[
                        {
                            field: "referenceCode",
                            headerName: t(
                                "certificatesTableHeader.referenceCode",
                            ),
                            renderHeader: () => (
                                <Box {...tableSearchProps}>
                                    <TextField
                                        {...tableTextFieldProps}
                                        id="referenceCode"
                                        label={t(
                                            "certificatesTableHeader.referenceCode",
                                        )}
                                        size="small"
                                        onChange={handleFilterChange}
                                        value={referenceCode}
                                        onBlur={() => checkFilterUpdateData()}
                                        onKeyDown={checkKeyEnter}
                                    />
                                    <TableSortLabel
                                        active={orderBy === "referenceCode"}
                                        direction={
                                            orderBy === "referenceCode"
                                                ? order
                                                : "desc"
                                        }
                                        onClick={() =>
                                            handleCertificateSort(
                                                "referenceCode",
                                            )
                                        }
                                    ></TableSortLabel>
                                </Box>
                            ),
                        },
                        {
                            field: "dateCreated",
                            headerName: t(
                                "certificatesTableHeader.dateCreated",
                            ),
                            renderHeader: () => (
                                <Box {...tableSearchDateProps}>
                                    <LocalizationProvider
                                        adapterLocale={language}
                                        dateAdapter={AdapterDayjs}
                                    >
                                        <DatePicker
                                            label={t(
                                                "certificatesTableHeader.dateCreated",
                                            )}
                                            value={dateCreated}
                                            onChange={(
                                                newValue,
                                                keyboardInputValue,
                                            ) => {
                                                handleDateFilterChange(
                                                    newValue,
                                                    keyboardInputValue,
                                                    "dateCreated",
                                                );
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    size="small"
                                                    /*onKeyDown={checkKeyEnter}*/ sx={{
                                                        backgroundColor:
                                                            "white",
                                                        borderRadius: "5px",
                                                    }}
                                                />
                                            )}
                                            inputFormat="DD/MM/YYYY"
                                        />
                                    </LocalizationProvider>

                                    <TableSortLabel
                                        active={orderBy === "creationTime"}
                                        direction={
                                            orderBy === "creationTime"
                                                ? order
                                                : "desc"
                                        }
                                        onClick={() =>
                                            handleCertificateSort(
                                                "creationTime",
                                            )
                                        }
                                    ></TableSortLabel>
                                </Box>
                            ),
                            valueFormatter: (params) =>
                                dayjs(params.value).format("DD/MM/YYYY"),
                        },
                        {
                            field: "firstNameRequester",
                            headerName: t("certificatesTableHeader.requester"),
                            renderHeader: () => (
                                <Box {...tableSearchProps}>
                                    <TextField
                                        {...tableTextFieldProps}
                                        id="requester"
                                        label={t(
                                            "certificatesTableHeader.requester",
                                        )}
                                        size="small"
                                        onChange={handleFilterChange}
                                        value={requester}
                                        onBlur={() => checkFilterUpdateData()}
                                        onKeyDown={checkKeyEnter}
                                    />
                                    <TableSortLabel
                                        active={
                                            orderBy ===
                                            "application.applicant.firstName"
                                        }
                                        direction={
                                            orderBy ===
                                            "application.applicant.firstName"
                                                ? order
                                                : "desc"
                                        }
                                        onClick={() =>
                                            handleCertificateSort(
                                                "application.applicant.firstName",
                                            )
                                        }
                                    ></TableSortLabel>
                                </Box>
                            ),
                            renderCell: (params) => (
                                <Box>
                                    {params.row.firstNameRequester}{" "}
                                    {params.row.lastNameRequester}
                                    {params.row.isRenewed ? (
                                        <Tooltip
                                            title={t(
                                                "certificates.renewedTooltip",
                                            )}
                                            arrow
                                        >
                                            <SvgIcon
                                                sx={{
                                                    paddingLeft: "2px",
                                                    fontSize: "16px",
                                                    verticalAlign: "top",
                                                }}
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 640 640"
                                                >
                                                    <path d="M286 368C384.5 368 464.3 447.8 464.3 546.3C464.3 562.7 451 576 434.6 576L78 576C61.6 576 48.3 562.7 48.3 546.3C48.3 447.8 128.1 368 226.6 368L286 368zM585.7 169.9C593.5 159.2 608.5 156.8 619.2 164.6C629.9 172.4 632.3 187.4 624.5 198.1L522.1 338.9C517.9 344.6 511.4 348.3 504.4 348.7C497.4 349.1 490.4 346.5 485.5 341.4L439.1 293.4C429.9 283.9 430.1 268.7 439.7 259.5C449.2 250.3 464.4 250.6 473.6 260.1L500.1 287.5L585.7 169.8zM256.3 312C190 312 136.3 258.3 136.3 192C136.3 125.7 190 72 256.3 72C322.6 72 376.3 125.7 376.3 192C376.3 258.3 322.6 312 256.3 312z" />
                                                </svg>
                                            </SvgIcon>
                                        </Tooltip>
                                    ) : (
                                        ""
                                    )}
                                </Box>
                            ),
                        },
                        {
                            field: "locations",
                            headerName: t("certificatesTableHeader.locations"),
                            renderHeader: () => (
                                <Box {...tableSearchProps}>
                                    <FormControl {...statusFilterProps}>
                                        <InputLabel id="locations-label">
                                            {t(
                                                "certificatesTableHeader.locations",
                                            )}
                                        </InputLabel>
                                        <Select
                                            labelId="locations-label"
                                            id="locations"
                                            multiple
                                            value={location}
                                            autoWidth
                                            onChange={(
                                                event: SelectChangeEvent<
                                                    string[]
                                                >,
                                            ) => {
                                                handleChange(
                                                    event,
                                                    "locations",
                                                );
                                            }}
                                            input={
                                                <OutlinedInput
                                                    label={t(
                                                        "certificatesTableHeader.locations",
                                                    )}
                                                />
                                            }
                                            renderValue={(selected) =>
                                                selected.join(", ")
                                            }
                                            MenuProps={tableFilterProps}
                                            onClose={() => {
                                                setCurrentPage(0);
                                                checkFilterUpdateData();
                                            }}
                                            onBlur={(e) => {
                                                e.stopPropagation();
                                            }}
                                        >
                                            {locations.map((item) => (
                                                <MenuItem
                                                    key={item.id}
                                                    value={item.name}
                                                    sx={{
                                                        paddingRight: "15px",
                                                    }}
                                                >
                                                    <Checkbox
                                                        checked={
                                                            location.indexOf(
                                                                item.name,
                                                            ) > -1
                                                        }
                                                    />
                                                    <ListItemText
                                                        primary={item.name}
                                                    />
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                            ),
                            renderCell: (params) => (
                                <Box
                                    sx={{
                                        paddingTop: "0.50rem",
                                        paddingBottom: "0.50rem",
                                    }}
                                >
                                    {params.value.map((location: any) => (
                                        <Box
                                            key={location.id}
                                            sx={{ textAlign: "center" }}
                                        >
                                            {location.name}
                                        </Box>
                                    ))}
                                </Box>
                            ),
                        },
                        {
                            field: "dateFrom",
                            headerName: t("certificatesTableHeader.startDate"),
                            renderHeader: () => (
                                <Box {...tableSearchProps}>
                                    <LocalizationProvider
                                        adapterLocale={language}
                                        dateAdapter={AdapterDayjs}
                                        size="small"
                                    >
                                        <DatePicker
                                            label={t(
                                                "certificatesTableHeader.startDate",
                                            )}
                                            value={dateFrom}
                                            onChange={(
                                                newValue,
                                                keyboardInputValue,
                                            ) => {
                                                handleDateFilterChange(
                                                    newValue,
                                                    keyboardInputValue,
                                                    "dateFrom",
                                                );
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    size="small"
                                                    /*onKeyDown={checkKeyEnter}*/ {...tableTextFieldProps}
                                                />
                                            )}
                                            inputFormat="DD/MM/YYYY"
                                        />
                                    </LocalizationProvider>

                                    <TableSortLabel
                                        active={
                                            orderBy === "application.dateFrom"
                                        }
                                        direction={
                                            orderBy === "application.dateFrom"
                                                ? order
                                                : "desc"
                                        }
                                        onClick={() =>
                                            handleCertificateSort(
                                                "application.dateFrom",
                                            )
                                        }
                                    ></TableSortLabel>
                                </Box>
                            ),
                            valueFormatter: (params) =>
                                dayjs(params.value).format("DD/MM/YYYY"),
                        },
                        {
                            field: "dateTo",
                            headerName: t("certificatesTableHeader.endDate"),
                            renderHeader: () => (
                                <Box {...tableSearchProps}>
                                    <LocalizationProvider
                                        adapterLocale={language}
                                        dateAdapter={AdapterDayjs}
                                        size="small"
                                    >
                                        <DatePicker
                                            label={t(
                                                "certificatesTableHeader.endDate",
                                            )}
                                            value={dateTo}
                                            onChange={(
                                                newValue,
                                                keyboardInputValue,
                                            ) => {
                                                handleDateFilterChange(
                                                    newValue,
                                                    keyboardInputValue,
                                                    "dateTo",
                                                );
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    size="small"
                                                    /*onKeyDown={checkKeyEnter}*/ {...tableTextFieldProps}
                                                />
                                            )}
                                            inputFormat="DD/MM/YYYY"
                                        />
                                    </LocalizationProvider>

                                    <TableSortLabel
                                        active={
                                            orderBy === "application.dateTo"
                                        }
                                        direction={
                                            orderBy === "application.dateTo"
                                                ? order
                                                : "desc"
                                        }
                                        onClick={() =>
                                            handleCertificateSort(
                                                "application.dateTo",
                                            )
                                        }
                                    ></TableSortLabel>
                                </Box>
                            ),
                            valueFormatter: (params) =>
                                dayjs(params.value).format("DD/MM/YYYY"),
                        },
                        {
                            field: "department",
                            headerName: t("requestsTableHeader.department"),
                            renderHeader: () => (
                                <Box {...tableSearchProps}>
                                    <FormControl {...statusFilterProps}>
                                        <InputLabel id="department-label">
                                            {t(
                                                "requestsTableHeader.department",
                                            )}
                                        </InputLabel>
                                        <Select
                                            labelId="department-label"
                                            multiple
                                            value={selectedDepartments}
                                            onChange={handleDepartmentChange}
                                            renderValue={(selected) =>
                                                selected.join(", ")
                                            }
                                            input={
                                                <OutlinedInput
                                                    label={t(
                                                        "requestsTableHeader.department",
                                                    )}
                                                />
                                            }
                                            MenuProps={tableFilterProps}
                                            onClose={() => {
                                                setCurrentPage(0);
                                                checkFilterUpdateData();
                                            }}
                                        >
                                            {departments.map((department) => (
                                                <MenuItem
                                                    key={department}
                                                    value={department}
                                                >
                                                    <Checkbox
                                                        checked={
                                                            selectedDepartments.indexOf(
                                                                department,
                                                            ) > -1
                                                        }
                                                    />
                                                    <ListItemText
                                                        primary={department}
                                                    />
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <TableSortLabel
                                        active={orderBy === "user.department"}
                                        direction={
                                            orderBy === "user.department"
                                                ? order
                                                : "desc"
                                        }
                                        onClick={() =>
                                            handleCertificateSort(
                                                "user.department",
                                            )
                                        }
                                    ></TableSortLabel>
                                </Box>
                            ),
                            valueFormatter: (params) => params.value,
                        },
                        {
                            field: "lastModified",
                            headerName: t(
                                "certificatesTableHeader.lastModificationTime",
                            ),
                            renderHeader: () => (
                                <Box {...tableSearchProps}>
                                    <LocalizationProvider
                                        adapterLocale={language}
                                        dateAdapter={AdapterDayjs}
                                        size="small"
                                    >
                                        <DatePicker
                                            label={t(
                                                "certificatesTableHeader.lastModificationTime",
                                            )}
                                            onChange={(
                                                newValue,
                                                keyboardInputValue,
                                            ) => {
                                                handleDateFilterChange(
                                                    newValue,
                                                    keyboardInputValue,
                                                    "modification",
                                                );
                                            }}
                                            value={lastModificationTime}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    {...tableTextFieldProps}
                                                    size="small"
                                                />
                                            )}
                                            inputFormat="DD/MM/YYYY"
                                        />
                                    </LocalizationProvider>
                                    <TableSortLabel
                                        active={orderBy === "lastModified"}
                                        direction={
                                            orderBy === "lastModified"
                                                ? order
                                                : "desc"
                                        }
                                        onClick={() =>
                                            handleCertificateSort(
                                                "lastModified",
                                            )
                                        }
                                    ></TableSortLabel>
                                </Box>
                            ),
                            renderCell: (params) => (
                                <Box>
                                    {params.row.lastModified
                                        ? dayjs(params.row.lastModified).format(
                                              "DD/MM/YYYY",
                                          )
                                        : dayjs(params.row.dateCreated).format(
                                              "DD/MM/YYYY",
                                          )}
                                </Box>
                            ),
                        },
                        {
                            field: "organization",
                            headerName: t("requestsTableHeader.org"),
                            renderHeader: () => (
                                <Box {...tableSearchProps}>
                                    {organisations.length > 0 ? (
                                        <FormControl
                                            {...organisationsFilterProps}
                                        >
                                            <InputLabel id="organisations-label">
                                                {t("requestsTableHeader.org")}
                                            </InputLabel>
                                            <Select
                                                labelId="organisations-label"
                                                multiple
                                                value={selectedOrganisations}
                                                onChange={handleSelectOrgChange}
                                                renderValue={(selected) =>
                                                    selected.join(", ")
                                                }
                                                input={
                                                    <OutlinedInput
                                                        label={t(
                                                            "requestsTableHeader.org",
                                                        )}
                                                    />
                                                }
                                            >
                                                {organisations.map(
                                                    (organisation) => (
                                                        <MenuItem
                                                            key={organisation}
                                                            value={organisation}
                                                        >
                                                            <Checkbox
                                                                checked={selectedOrganisations.includes(
                                                                    organisation,
                                                                )}
                                                            />
                                                            <ListItemText
                                                                primary={
                                                                    organisation
                                                                }
                                                            />
                                                        </MenuItem>
                                                    ),
                                                )}
                                            </Select>
                                        </FormControl>
                                    ) : (
                                        <TextField
                                            {...tableTextFieldProps}
                                            size="small"
                                            id="organisation"
                                            label={t("requestsTableHeader.org")}
                                            value={organisation}
                                            disabled={organisationDisabled}
                                            onChange={handleOrgChange}
                                            onBlur={() => {
                                                setCurrentPage(0);
                                                checkFilterUpdateData();
                                            }}
                                            onKeyDown={checkKeyEnter}
                                        />
                                    )}
                                    <TableSortLabel
                                        active={orderBy === "organisation"}
                                        direction={
                                            orderBy === "organisation"
                                                ? order
                                                : "desc"
                                        }
                                        onClick={() =>
                                            handleCertificateSort(
                                                "organisation",
                                            )
                                        }
                                    ></TableSortLabel>
                                </Box>
                            ),
                            valueFormatter: (params) => params.value,
                        },
                        {
                            field: "status",
                            headerName: t("certificatesTableHeader.status"),
                            renderHeader: () => (
                                <Box {...tableSearchProps}>
                                    <FormControl {...statusFilterProps}>
                                        <InputLabel id="status-label">
                                            {t(
                                                "certificatesTableHeader.status",
                                            )}
                                        </InputLabel>
                                        <Select
                                            labelId="status-label"
                                            id="status"
                                            multiple
                                            value={status}
                                            onChange={(
                                                event: SelectChangeEvent<
                                                    string[]
                                                >,
                                            ) => {
                                                handleChange(event, "status");
                                            }}
                                            input={
                                                <OutlinedInput
                                                    label={t(
                                                        "certificatesTableHeader.status",
                                                    )}
                                                />
                                            }
                                            renderValue={(selected) =>
                                                selected
                                                    .map((s) => t(s))
                                                    .join(", ")
                                            }
                                            MenuProps={tableFilterProps}
                                            onClose={() => {
                                                setCurrentPage(0);
                                                checkFilterUpdateData();
                                            }}
                                            onBlur={(e) => {
                                                e.stopPropagation();
                                            }}
                                        >
                                            {statuses.map((name) => (
                                                <MenuItem
                                                    key={name}
                                                    value={name}
                                                >
                                                    <Checkbox
                                                        checked={
                                                            status.indexOf(
                                                                name,
                                                            ) > -1
                                                        }
                                                    />
                                                    <ListItemText
                                                        primary={t(name)}
                                                    />
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <TableSortLabel
                                        active={orderBy === "status"}
                                        direction={
                                            orderBy === "status"
                                                ? order
                                                : "desc"
                                        }
                                        onClick={() =>
                                            handleCertificateSort("status")
                                        }
                                    ></TableSortLabel>
                                </Box>
                            ),
                            renderCell: (params) => (
                                <Chip
                                    sx={{
                                        marginLeft: "10px",
                                        backgroundColor: statusStyleCertificate(
                                            getStatus(params.row),
                                        ),
                                        color: "white",
                                    }}
                                    label={t(getStatus(params.row))}
                                    size="small"
                                />
                            ),
                        },
                    ]}
                    rowCount={count}
                    onRowClick={(params) =>
                        handleClickCertificate(
                            params.row as CertificatePagedDTO,
                        )
                    }
                    loading={isLoading}
                    columnVisibilityModel={columns}
                    onColumnVisibilityModelChange={handleColumnVisibility}
                    updatePagination={updatePagination}
                    paginationModel={{
                        pageSize: rowsPerPage,
                        page: currentPage,
                    }}
                    initialColumnVisibility={initialColumnVisibility}
                    reset={handleReset}
                    autoHeight={["locations", "organization"]}
                />
            </Box>
        </Card>
    );
};

export default ListCertificates;
